import { Fragment, useState } from 'react'
import {
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from 'flowbite-react'
import {
  EXPORT_FORMAT_LABELS,
  EXPORT_STATUS_LABELS,
  GROUPING_LABELS,
  PLAYER_FILTER_LABELS,
} from '@constants/exports'
import { type ExportJob } from '@app-types/stores/exports'

interface ExportHistoryTableProps {
  isLoading: boolean
  jobs: ExportJob[]
}

function ExportHistoryTable({ isLoading, jobs }: ExportHistoryTableProps) {
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null)

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
      <div className="border-b border-slate-200 px-5 py-4 dark:border-zinc-800">
        <h2 className="m-0 text-lg font-semibold tracking-normal text-slate-950 dark:text-slate-100">
          История выгрузок
        </h2>
      </div>

      <div className="overflow-x-auto">
        <Table hoverable className="min-w-max text-sm">
          <TableHead>
            <TableRow>
              <TableHeadCell className="bg-slate-50 text-xs dark:bg-zinc-900">
                Дата выгрузки
              </TableHeadCell>
              <TableHeadCell className="bg-slate-50 text-xs dark:bg-zinc-900">
                Игры
              </TableHeadCell>
              <TableHeadCell className="bg-slate-50 text-xs dark:bg-zinc-900">
                По игрокам
              </TableHeadCell>
              <TableHeadCell className="bg-slate-50 text-xs dark:bg-zinc-900">
                Формат
              </TableHeadCell>
              <TableHeadCell className="bg-slate-50 text-xs dark:bg-zinc-900">
                Группировка
              </TableHeadCell>
              <TableHeadCell className="bg-slate-50 text-xs dark:bg-zinc-900">
                Статус
              </TableHeadCell>
              <TableHeadCell className="bg-slate-50 text-xs dark:bg-zinc-900">
                Файл
              </TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody className="divide-y">
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center">
                  <div className="inline-flex items-center gap-3 text-slate-500 dark:text-slate-400">
                    <Spinner size="md" />
                    <span>Загрузка...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : null}

            {!isLoading && jobs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-8 text-center text-slate-500 dark:text-slate-400"
                >
                  Пока нет ни одной выгрузки.
                </TableCell>
              </TableRow>
            ) : null}

            {!isLoading
              ? jobs.map((job) => (
                  <Fragment key={job.id}>
                    <TableRow className="bg-white dark:border-zinc-800 dark:bg-zinc-900">
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            aria-expanded={expandedJobId === job.id}
                            aria-label={
                              expandedJobId === job.id
                                ? 'Скрыть детали выгрузки'
                                : 'Показать детали выгрузки'
                            }
                            className="grid size-7 place-items-center rounded-md border border-slate-200 bg-white text-base font-bold text-slate-700 transition hover:bg-slate-100 dark:border-zinc-700 dark:bg-zinc-950 dark:text-slate-200 dark:hover:bg-zinc-800"
                            onClick={() =>
                              setExpandedJobId(
                                expandedJobId === job.id ? null : job.id,
                              )
                            }
                            type="button"
                          >
                            {expandedJobId === job.id ? '-' : '+'}
                          </button>
                          <span>{job.exportedAt}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {job.games.length ? job.games.join(', ') : 'Все игры'}
                      </TableCell>
                      <TableCell>
                        {PLAYER_FILTER_LABELS[job.playerFilter]}
                      </TableCell>
                      <TableCell>
                        {EXPORT_FORMAT_LABELS[job.exportFormat]}
                      </TableCell>
                      <TableCell>{GROUPING_LABELS[job.grouping]}</TableCell>
                      <TableCell>{EXPORT_STATUS_LABELS[job.status]}</TableCell>
                      <TableCell>
                        {job.fileUrl ? (
                          <a
                            className="font-semibold text-violet-700 no-underline hover:underline dark:text-violet-300"
                            href={job.fileUrl}
                          >
                            Скачать файл
                          </a>
                        ) : (
                          <span className="text-slate-500 dark:text-slate-400">
                            -
                          </span>
                        )}
                      </TableCell>
                    </TableRow>

                    {expandedJobId === job.id ? (
                      <TableRow className="bg-slate-50 dark:border-zinc-800 dark:bg-zinc-950">
                        <TableCell colSpan={7}>
                          <dl className="m-0 flex flex-wrap gap-3 text-sm">
                            <div className="flex items-baseline gap-2">
                              <dt className="font-semibold text-slate-500 dark:text-slate-400">
                                Пароль архива
                              </dt>
                              <dd className="m-0 font-mono font-semibold text-slate-800 dark:text-slate-100">
                                {job.archivePassword || '-'}
                              </dd>
                            </div>
                            {job.fileName ? (
                              <div className="flex items-baseline gap-2">
                                <dt className="font-semibold text-slate-500 dark:text-slate-400">
                                  Файл
                                </dt>
                                <dd className="m-0 font-mono text-slate-800 dark:text-slate-100">
                                  {job.fileName}
                                </dd>
                              </div>
                            ) : null}
                          </dl>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                ))
              : null}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default ExportHistoryTable
